import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Spin, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { controlRefPlaceholder, LIBRARY_STATUS_LABELS } from '../../utils/libraryTemplateModel';

const { Text, Paragraph } = Typography;

/**
 * "Fetch, then curate": step 1 reads the platform's OWN Policy Hub master row for a control
 * (GET /admin/library-templates/sources — no external fetch, nothing un-curated enters the
 * prompt); step 2 asks AI to draft a v1 template from that grounding
 * (POST /admin/library-templates/ai-draft). The result is stored as a DRAFT AI row with
 * `reviewedBy: null` — it goes nowhere until a human approves and publishes it (Commandment IV).
 * The API refuses to draft when the master catalog has no such control (no grounding, no draft)
 * and when any version already exists for that control.
 *
 * platform_admin only (server-enforced). `presetStandardId/presetControlRef` let the coverage
 * view open this straight on a gap.
 */
const LibraryAiDraftModal = ({ open, standardOptions, presetStandardId, presetControlRef, onClose, onDrafted }) => {
    const { request } = useApiRequest();
    const [standardId, setStandardId] = useState(null);
    const [controlRef, setControlRef] = useState('');
    const [note, setNote] = useState('');
    const [source, setSource] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setStandardId(presetStandardId || null);
        setControlRef(presetControlRef || '');
        setNote('');
        setSource(null);
        setError(null);
    }, [open, presetStandardId, presetControlRef]);

    const fetchSource = async () => {
        if (!standardId || !controlRef.trim()) return setError('Choose a standard and enter a control reference.');
        setFetching(true);
        setError(null);
        setSource(null);
        try {
            const { data } = await request('GET', '/admin/library-templates/sources', {
                query: { standardId, controlRef: controlRef.trim() },
            });
            setSource(data);
        } catch (err) {
            setError(err.message || 'Could not fetch the Policy Hub control.');
        } finally {
            setFetching(false);
        }
    };

    const draft = async () => {
        setDrafting(true);
        setError(null);
        try {
            const { data } = await request('POST', '/admin/library-templates/ai-draft', {
                body: { standardId, controlRef: controlRef.trim(), curatorNote: note.trim() || undefined },
            });
            message.success('AI draft created. Review it before submitting - nothing is published automatically.');
            onDrafted(data.item);
        } catch (err) {
            setError(err.message || 'The AI draft failed.');
        } finally {
            setDrafting(false);
        }
    };

    const hasExisting = (source?.items || []).length > 0;

    return (
        <Modal open={open} width={760} title="Draft a template with AI" onCancel={onClose} destroyOnClose footer={null}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Select
                        aria-label="Standard"
                        showSearch
                        optionFilterProp="label"
                        style={{ minWidth: 260 }}
                        value={standardId}
                        onChange={(v) => {
                            setStandardId(v);
                            setSource(null);
                        }}
                        options={standardOptions}
                        placeholder="Standard"
                    />
                    <Input
                        aria-label="Control reference"
                        style={{ width: 220 }}
                        value={controlRef}
                        onChange={(e) => {
                            setControlRef(e.target.value);
                            setSource(null);
                        }}
                        placeholder={controlRefPlaceholder(standardId)}
                    />
                    <Button loading={fetching} onClick={fetchSource}>Fetch from Policy Hub</Button>
                </div>

                {fetching && <Spin />}
                {source && (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12 }} data-testid="policy-hub-source">
                        <Text strong>{source.control.controlRef} - {source.control.controlName}</Text>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Grounding: Policy Hub {source.provenance.catalog === 'iso27001_controls' ? 'ISO catalogue' : 'framework catalogue'} · {source.provenance.versionLabel}
                                {source.family ? ` · ${source.family}` : ''}
                            </Text>
                        </div>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8, marginBottom: 4 }} ellipsis={{ rows: 5, expandable: true, symbol: 'more' }}>
                            {source.control.definition}
                        </Paragraph>
                        {source.control.guidance && (
                            <Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }} ellipsis={{ rows: 4, expandable: true, symbol: 'more' }}>
                                {source.control.guidance}
                            </Paragraph>
                        )}
                    </div>
                )}
                {source && hasExisting && (
                    <Alert
                        type="warning"
                        showIcon
                        message="A template already exists for this control"
                        description={`Existing versions: ${source.items.map((i) => `v${i.version} (${LIBRARY_STATUS_LABELS[i.status] || i.status})`).join(', ')}. AI drafting only creates a first version - open the existing template to AI-revise a draft, or fork a new version of a published one.`}
                    />
                )}
                {source && !hasExisting && (
                    <>
                        <div>
                            <Text strong>Note to the drafter (optional)</Text>
                            <Input.TextArea
                                aria-label="Curator note"
                                rows={3}
                                maxLength={1000}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. Aimed at Indian IT services firms; mention RBI CSF where relevant."
                            />
                        </div>
                        <Alert
                            type="info"
                            showIcon
                            message="The result is an AI draft"
                            description="It is saved as a draft. No AI agreement score is computed for library drafts; the only automated check is a clause-reference check against the Policy Hub text, and it is not available for every standard. It stays unpublished until you review it and take it through Submit, Approve and Publish. Drafting can take a minute."
                        />
                        <div>
                            <Button type="primary" loading={drafting} onClick={draft}>Draft with AI</Button>
                        </div>
                    </>
                )}
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default LibraryAiDraftModal;
